// reducers/employeeSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  attachApiErrorToast,
  getApiErrorMessage,
  notifyApiSuccessFrom,
} from "../../helpers/apiNotify";
import axios from "axios";
import qs from "qs";
import _ from "lodash";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});
attachApiErrorToast(API, { fallback: "Employee request failed" });

const initialState = {
  createPendingCount: 0,
  createLoading: false,
  fetchLoading: false,
  applicationsLoading: false,
  createError: null,
  fetchError: null,
  message: null,
  users: [],
  user: {},
  applications: [],
  allEmployees: [],
  allEmployeesLoading: false,
};

const bumpCreatePending = (state, delta) => {
  state.createPendingCount = Math.max(0, (state.createPendingCount || 0) + delta);
  state.createLoading = state.createPendingCount > 0;
};

export const addEmployee = createAsyncThunk(
  "employee/addEmployee",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${data.accessToken}`,
          "Content-Type": "application/json",
        },
      };

      const response = await API.post("/users", data.userData, config);
      notifyApiSuccessFrom(response, "Employee created!");
      return response.data;
    } catch (error) {
      // Toast already shown by interceptor with API message
      throw new Error(getApiErrorMessage(error, "Failed to add employee"));
    }
  }
);

export const adduser = createAsyncThunk("employee/adduser", async (data) => {
  try {
    const config = {
      headers: {
        "ngrok-skip-browser-warning": "69420",
        Authorization: `${data.accessToken}`,
        "Content-Type": "application/json",
      },
    };

    const response = await API.post("/users", data.userData, config);
    notifyApiSuccessFrom(response, "User created!");
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to add employee"));
  }
});

export const fetchEmployee = createAsyncThunk(
  "employee/fetchEmployee",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };
      let url = "/users?roles=employee";
      if (data?.queryParams && !_.isEmpty(data?.queryParams)) {
        url += `&${data?.queryParams instanceof Object
          ? qs.stringify(data?.queryParams)
          : data?.queryParams
          }`;
      }
      const response = await API.get(url, config);
      return response.data;
    } catch (error) {
      throw new Error("Failed to load employee");
    }
  }
);
export const fetchEmployeeById = createAsyncThunk(
  "employee/fetchEmployeeById",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };
      const response = await API.get(`/users/${data.userId}?roles=employee`, config);
      return response.data;
    } catch (error) {
      throw new Error("Failed to load employee");
    }
  }
);

export const deleteEmployee = createAsyncThunk(
  "employee/deleteEmployee",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };
      const response = await API.delete(
        `/users/delete/${data.id}`,
        { delete_message: data.delete_message },
        config
      );
      data?.setShowDeleteComfirmModal(true);
      data?.setDeleteLoader(false);
      notifyApiSuccessFrom(response, "Employee deleted!");
      return response.data;
      // return
    } catch (error) {
      throw new Error("Failed to delete employee");
    }
  }
);

// Async thunk for fetching company applications
export const fetchEmployeeApplications = createAsyncThunk(
  "employee/fetchEmployeeApplications",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };
      const response = await API.get(
        `/applications/user/${data.userId}`,
        config
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load employee");
    }
  }
);

/**
 * Fetches all employees without pagination to calculate accurate total counts
 */
export const fetchAllEmployeesForCounts = createAsyncThunk(
  "employee/fetchAllEmployeesForCounts",
  async ({ accessToken }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${accessToken}`,
        },
      };

      let allEmployees = [];
      let currentPage = 1;
      let hasMorePages = true;
      const pageSize = 100;

      while (hasMorePages) {
        // Same role filter as fetchEmployee — company panel "Employees" must not count admins/reviewers
        const url = `/users?roles=employee&page=${currentPage}&limit=${pageSize}`;
        const response = await API.get(url, config);
        const responseData = response.data;

        let pageUsers = [];
        if (Array.isArray(responseData)) {
          pageUsers = responseData;
        } else if (Array.isArray(responseData?.data)) {
          pageUsers = responseData.data;
        } else if (Array.isArray(responseData?.users)) {
          pageUsers = responseData.users;
        }

        allEmployees = allEmployees.concat(pageUsers);

        const totalPages =
          responseData?.totalPages ||
          responseData?.pagination?.total_pages ||
          responseData?.pagination?.totalPages ||
          null;

        if (totalPages != null) {
          hasMorePages = currentPage < totalPages;
        } else if (pageUsers.length < pageSize) {
          hasMorePages = false;
        } else {
          hasMorePages = currentPage < 50;
        }
        currentPage += 1;
      }

      return allEmployees;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch all employees"
      );
    }
  }
);

const employeeSlice = createSlice({
  name: "employee",
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder
      .addCase(addEmployee.pending, (state) => {
        bumpCreatePending(state, 1);
      })
      .addCase(addEmployee.fulfilled, (state, action) => {
        bumpCreatePending(state, -1);
        state.message = action.payload?.message;
        state.createError = null;
      })
      .addCase(adduser.pending, (state) => {
        bumpCreatePending(state, 1);
      })
      .addCase(adduser.fulfilled, (state, action) => {
        bumpCreatePending(state, -1);
        state.message = action.payload?.message;
        state.createError = null;
      })
      .addCase(adduser.rejected, (state, action) => {
        bumpCreatePending(state, -1);
        state.createError = action.error?.message;
        state.message = action.error?.message;
      })
      .addCase(addEmployee.rejected, (state, action) => {
        bumpCreatePending(state, -1);
        state.createError = action.error?.message;
        state.message = action.error?.message;
      })
      .addCase(fetchEmployee.pending, (state) => {
        state.fetchLoading = true;
      })
      .addCase(fetchEmployee.fulfilled, (state, action) => {
        state.fetchLoading = false;
        // Handle both array (old) and object with data property (new) responses
        state.users = Array.isArray(action.payload) ? action.payload : (action.payload?.data || action.payload?.users || []);
        state.fetchError = null;
      })
      .addCase(fetchEmployee.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.error?.message;
        state.users = initialState.users;
        state.message = null;
      })
      .addCase(fetchEmployeeById.pending, (state) => {
        state.fetchLoading = true;
      })
      .addCase(fetchEmployeeById.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.user = action.payload;
        state.fetchError = null;
      })
      .addCase(fetchEmployeeById.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.error?.message;
        state.user = initialState.user;
        state.message = null;
      })
      .addCase(fetchEmployeeApplications.pending, (state) => {
        state.applicationsLoading = true;
      })
      .addCase(fetchEmployeeApplications.fulfilled, (state, action) => {
        state.applicationsLoading = false;
        state.applications = action.payload;
        state.fetchError = null;
      })
      .addCase(fetchEmployeeApplications.rejected, (state, action) => {
        state.applicationsLoading = false;
        state.fetchError = action.error?.message;
        state.applications = initialState.applications;
        state.message = null;
      })
      .addCase(fetchAllEmployeesForCounts.pending, (state) => {
        state.allEmployeesLoading = true;
      })
      .addCase(fetchAllEmployeesForCounts.fulfilled, (state, action) => {
        state.allEmployeesLoading = false;
        state.allEmployees = action.payload;
        state.createError = null;
      })
      .addCase(fetchAllEmployeesForCounts.rejected, (state, action) => {
        state.allEmployeesLoading = false;
        state.createError = action.payload;
      });
  },
});

export default employeeSlice.reducer;
